/**
 * Email Verification Link Page for v0.10.1
 * Handles email verification via JWT token link
 * Luxury glass morphism design
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyEmailLink } from '../../services/emailApi';
import './VerifyEmailLinkPage.css';

interface VerifyEmailLinkPageProps {
  className?: string;
}

const VerifyEmailLinkPage: React.FC<VerifyEmailLinkPageProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus('error');
        setMessage('驗證連結無效，請重新申請驗證 Email');
        return;
      }

      try {
        const response = await verifyEmailLink(token);
        if (response.success && response.data?.verified) {
          setStatus('success');
          setMessage('Email 驗證成功！您現在可以完整使用所有功能。');
          // Redirect to member center after 3 seconds
          setTimeout(() => navigate('/member', { replace: true }), 3000);
        } else {
          setStatus('error');
          setMessage(response.data?.message || '驗證失敗，可能是連結已過期或無效');
        }
      } catch (err: any) {
        setStatus('error');
        setMessage(err?.error?.message || '驗證失敗，請重新申請驗證 Email');
      }
    };

    verifyToken();
  }, [token, navigate]);

  const handleGoToMemberCenter = () => {
    navigate('/member', { replace: true });
  };

  const handleRequestNewLink = () => {
    navigate('/member/settings', { replace: true });
  };

  return (
    <div className="verify-email-link-page">
      <div className="verify-email-link-page__container">
        {/* Logo and Title - always shown */}
        <div className="verify-email-link-page__logo">
          🚗
        </div>
        <h1 className="verify-email-link-page__title">瑋瑋車業</h1>

        {/* Status-specific content */}
        {status === 'loading' && (
          <>
            <div className="verify-email-link-page__spinner"></div>
            <h2 className="verify-email-link-page__subtitle">正在驗證您的 Email...</h2>
            <p className="verify-email-link-page__message">請稍候片刻</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="verify-email-link-page__icon verify-email-link-page__icon--success">✓</div>
            <h2 className="verify-email-link-page__subtitle">Email 驗證成功！</h2>
            <p className="verify-email-link-page__message">{message}</p>
            <p className="verify-email-link-page__countdown">正在跳轉到會員中心...</p>
            <button
              onClick={handleGoToMemberCenter}
              className="verify-email-link-page__btn verify-email-link-page__btn--primary"
            >
              前往會員中心
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="verify-email-link-page__icon verify-email-link-page__icon--error">✕</div>
            <h2 className="verify-email-link-page__subtitle">驗證失敗</h2>
            <p className="verify-email-link-page__message">{message}</p>
            <div className="verify-email-link-page__actions">
              <button
                onClick={handleRequestNewLink}
                className="verify-email-link-page__btn verify-email-link-page__btn--primary"
              >
                重新申請驗證 Email
              </button>
              <button
                onClick={handleGoToMemberCenter}
                className="verify-email-link-page__btn verify-email-link-page__btn--secondary"
              >
                前往會員中心
              </button>
            </div>
          </>
        )}

        {/* Info Box */}
        {status !== 'loading' && (
          <div className="verify-email-link-page__info">
            <p className="verify-email-link-page__info-title">為什麼需要驗證 Email？</p>
            <ul>
              <li>確保您的帳號安全，防止他人使用您的 Email 註冊</li>
              <li>接收重要通知（如廣告審核結果、許願配對通知等）</li>
              <li>完整使用瑋瑋車業的所有功能</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailLinkPage;
